import { useState } from "react";
import { BellOff, Plus, Trash2, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useNotificationPolicies,
  useUpsertNotificationPolicy,
  useDeleteNotificationPolicy,
} from "@/hooks/use-api";
import {
  NOTIFICATION_CATEGORIES,
  type NotificationPolicy,
  type NotificationPolicyScope,
  type UpsertNotificationPolicyRequest,
} from "@/types/api";

const SELECT_CLASS =
  "mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

const DURATIONS = [
  { value: "permanent", label: "Permanent", days: null },
  { value: "7", label: "7 days", days: 7 },
  { value: "30", label: "30 days", days: 30 },
  { value: "90", label: "90 days", days: 90 },
] as const;

function mutedUntilFromDuration(value: string): string | null {
  const d = DURATIONS.find((x) => x.value === value);
  if (!d || d.days == null) return null;
  return new Date(Date.now() + d.days * 86_400_000).toISOString();
}

function describePolicy(p: NotificationPolicy): string {
  if (p.scope === "global") return "All rules (global)";
  if (p.scope === "category") return `Category: ${p.target}`;
  return `Rule: ${p.target}`;
}

export function AlertPolicyTab() {
  const [showForm, setShowForm] = useState(false);
  const [scope, setScope] = useState<NotificationPolicyScope>("category");
  const [target, setTarget] = useState("");
  const [channel, setChannel] = useState("all");
  const [duration, setDuration] = useState("30");
  const [reason, setReason] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data, isLoading, isError } = useNotificationPolicies();
  const upsert = useUpsertNotificationPolicy();
  const remove = useDeleteNotificationPolicy();
  const policies = data?.policies ?? [];

  function resetForm() {
    setShowForm(false);
    setScope("category");
    setTarget("");
    setChannel("all");
    setDuration("30");
    setReason("");
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const body: UpsertNotificationPolicyRequest = {
      scope,
      target: scope === "global" ? null : target.trim(),
      channel: channel === "all" ? null : (channel as UpsertNotificationPolicyRequest["channel"]),
      mode: "mute",
      muted_until: mutedUntilFromDuration(duration),
      reason: reason.trim() || null,
    };
    upsert.mutate(body, { onSuccess: resetForm });
  }

  function handleDelete(id: string) {
    if (!window.confirm("Remove this mute? Notifications for it will resume.")) return;
    setDeletingId(id);
    remove.mutate(id, { onSettled: () => setDeletingId(null) });
  }

  const submitDisabled = upsert.isPending || (scope !== "global" && target.trim() === "");

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Alert Policy</h2>
          <p className="text-sm text-muted-foreground">
            Mute notifications for a rule, category, or globally. Detection keeps running and muted
            alerts stay in history — only the outbound notification is suppressed.
          </p>
        </div>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} className="shrink-0">
            <Plus className="mr-1.5 h-4 w-4" /> Add mute
          </Button>
        )}
      </div>

      {showForm && (
        <Card>
          <CardContent className="p-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="policy-scope">Scope</Label>
                  <select
                    id="policy-scope"
                    value={scope}
                    onChange={(e) => setScope(e.target.value as NotificationPolicyScope)}
                    className={SELECT_CLASS}
                  >
                    <option value="category">Category</option>
                    <option value="rule">Specific rule</option>
                    <option value="global">All rules (global)</option>
                  </select>
                </div>

                {scope === "category" && (
                  <div>
                    <Label htmlFor="policy-category">Category</Label>
                    <select
                      id="policy-category"
                      value={target}
                      onChange={(e) => setTarget(e.target.value)}
                      className={SELECT_CLASS}
                    >
                      <option value="">Select a category…</option>
                      {NOTIFICATION_CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {scope === "rule" && (
                  <div>
                    <Label htmlFor="policy-rule">Rule ID</Label>
                    <Input
                      id="policy-rule"
                      value={target}
                      onChange={(e) => setTarget(e.target.value)}
                      placeholder="rule UUID — easier to mute from an alert"
                      className="mt-1.5"
                    />
                  </div>
                )}

                <div>
                  <Label htmlFor="policy-channel">Channel</Label>
                  <select
                    id="policy-channel"
                    value={channel}
                    onChange={(e) => setChannel(e.target.value)}
                    className={SELECT_CLASS}
                  >
                    <option value="all">All channels (incl. tickets)</option>
                    <option value="email">Email</option>
                    <option value="slack">Slack</option>
                    <option value="sms">SMS</option>
                    <option value="webhook">Webhook</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="policy-duration">Duration</Label>
                  <select
                    id="policy-duration"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    className={SELECT_CLASS}
                  >
                    {DURATIONS.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="policy-reason">Reason (optional)</Label>
                <Input
                  id="policy-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. public SSH is accepted for this environment"
                  className="mt-1.5"
                />
              </div>

              {upsert.isError && (
                <p className="text-sm text-destructive">
                  Could not save the mute. Check the rule/category and try again.
                </p>
              )}

              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={resetForm}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitDisabled}>
                  {upsert.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                  Mute
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : isError ? (
        <p className="text-sm text-destructive">Failed to load policies.</p>
      ) : policies.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <BellOff className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No mutes. Every rule notifies per your notification channels.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {policies.map((p) => (
            <Card key={p.id}>
              <CardContent className="flex items-center justify-between gap-4 p-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{describePolicy(p)}</span>
                    <Badge variant="secondary">{p.channel ?? "all channels"}</Badge>
                    <Badge variant="outline">
                      {p.muted_until
                        ? `until ${new Date(p.muted_until).toLocaleDateString()}`
                        : "permanent"}
                    </Badge>
                  </div>
                  {p.reason && (
                    <p className="mt-1 truncate text-sm text-muted-foreground">{p.reason}</p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(p.id)}
                  disabled={deletingId === p.id}
                  aria-label="Remove mute"
                >
                  {deletingId === p.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
